import React from "react";
import ModalLayout from "app/components/Layouts/ModalLayout/ModalLayout";
import styles from "./styles.module.scss";
import { useTranslation } from "react-i18next";
import { ProgramModel } from "app/models/scheduling/ProgramModel";
import { DateTime } from "luxon";
import UserAvatar from "../UserAvatar";
import { generateCurrencySymbolPriceAbbreviation } from "app/routes/BuildProgram/Pricing/utils";
import ButtonVariant from "app/components/Button/ButtonVariant";
import { useSelector } from "react-redux";
import { RootState } from "app/reducers";
import { toast } from "react-toastify";
import useCopyToClipboard from "app/utils/hooks/useCopyToClipboard";

interface ProgramDetailsModalProps {
  isOpen: boolean;
  onClose: () => void;
  programModel: ProgramModel;
  onRegister: () => void;
  onRegistrants: () => void;
  allowAdditionalPeople: boolean;
  isRegistered: boolean;
  /** A session that has already happened can be viewed, but not registered for. */
  isPassed?: boolean;
  /** CN-2612: false for a full program with no waitlist, which is opened only to view. */
  canRegister?: boolean;
}

const ProgramDetailsModal = ({
  isOpen,
  onClose,
  programModel,
  onRegister,
  onRegistrants,
  allowAdditionalPeople,
  isRegistered,
  isPassed = false,
  canRegister = true,
}: ProgramDetailsModalProps) => {
  const { t } = useTranslation();
  const { copyToClipboard } = useCopyToClipboard();
  const date = DateTime.fromISO(programModel?.start, {zone: programModel?.timeZone}).toFormat('EEEE MMMM d, yyyy');

  // Surface the payment link for the registered athlete viewing their own program.
  // The BE only ships invoice_url / share_payment_url on their own registration row,
  // so this naturally hides for unregistered viewers and for other athletes' rows.
  const currentUserId = useSelector((state: RootState) => state.user.data.id);
  const athleteRegistration = programModel?.completedRegistrants?.find(
    (reg) => reg.studentProfile.id === currentUserId,
  );
  const invoiceUrl = athleteRegistration?.invoiceUrl;
  const sharePaymentUrl = athleteRegistration?.sharePaymentUrl;

  const handleCopyPaymentLink = () => {
    if (sharePaymentUrl) {
      copyToClipboard(sharePaymentUrl);
      toast.success(t("Payment link copied"));
    }
  };

  return (
    <ModalLayout
      isOpen={isOpen}
      onClose={onClose}
      classNames={styles.modal_container}
    >
      <div className={styles.header}>
        <span>{t('CLINIC')}</span>
        <i className="ico ico-x" onClick={onClose} />
      </div>

      <div className={styles.body}>
        <div className={styles.left}>
          <div className={styles.title_date_container}>
            <p className={styles.title}>{programModel?.title}</p>
            <p className={styles.start_date}>{date}</p>
          </div>

          {programModel?.tags?.length > 0 && (
            <div className={styles.tags_container}>
              {programModel?.tags?.map((tag, index) => (
                <span key={index} className={styles.tag}>{t(tag.name)}</span>
              ))}
            </div>
          )}

          <p className={styles.description}>{t(programModel?.longDescription)}</p>
        </div>

        <div className={styles.right}>
          <div className={styles.coaches_container}>
            <p className={styles.details_header_coach}>{t("Coaches")}</p>
            <div className={styles.coaches_list}>
              {programModel?.coaches?.map((coach, index) => (
                <div className={styles.custom_coach_card} key={index}>
                  <div className={styles.custom_coach_card_content}>
                    <UserAvatar
                      src={coach?.profilePicture}
                      height={48}
                      font_size={12}
                      width={48}
                      first_name={coach?.firstName}
                      last_name={coach?.lastName}
                    />
                    <p className={styles.coach_name}>
                      {coach?.firstName} {coach?.lastName}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className={styles.contacts_container}>
            <p className={styles.contacts_title}>{t("Contact Info")}</p>
            <div className={styles.contacts_column_container}>
              <div className={styles.email_phone_container}>
                <i className="ico ico-mail-o" />
                <span>
                  {programModel?.email}
                </span>
              </div>
              <div className={styles.email_phone_container}>
                <i className="ico ico-phone" />
                <span>
                  {programModel?.phone}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <hr />

      {invoiceUrl && (
        <div className={styles.payment_link_row}>
          <a
            href={invoiceUrl}
            target="_blank"
            rel="noopener noreferrer"
            className={styles.payment_link}
          >
            {t("View invoice")}
          </a>
          {sharePaymentUrl && sharePaymentUrl !== invoiceUrl && (
            <>
              <span className={styles.payment_link_separator}>•</span>
              <a
                onClick={handleCopyPaymentLink}
                className={styles.payment_link}
                style={{ cursor: "pointer" }}
              >
                {t("Copy payment link")}
              </a>
            </>
          )}
        </div>
      )}

      <div className={styles.footer}>
        <div className={styles.price_container}>
          <p className={styles.price}>
            {programModel?.isFree
              ? t('Free')
              : generateCurrencySymbolPriceAbbreviation(programModel?.facilityCurrency, programModel?.price.toString())}
          </p>
          {!programModel?.isFree && <p className={styles.info_price}>{t('One time payment')}</p>}
        </div>

        <div className={styles.buttons_container}>
          <ButtonVariant
            buttonType="primary-filled"
            title={t('Registrants and Waitlist')}
            onClickFunc={onRegistrants}
            buttonStyles={styles.web_button_register}
          />
          <ButtonVariant
            buttonType="primary-filled"
            title={t('Registrants')}
            onClickFunc={onRegistrants}
            buttonStyles={styles.mobile_register_button}
          />
          <ButtonVariant
            buttonType="primary-filled"
            title={t('Register')}
            onClickFunc={onRegister}
            disabled={!canRegister || isPassed || (!allowAdditionalPeople && isRegistered)}
          />
        </div>
      </div>
    </ModalLayout>
  );
};

export default ProgramDetailsModal;
